export type DateRangeBound = {
  dateType: Nullable<string>;
  value: string | number | null;
};

export enum DateRangeBoundType {
  SpecificDate = "specific date",
  Yesterday = "yesterday",
  Today = "today",
  Tomorrow = "tomorrow",
  LastNDays = "last [n] days",
  LastNWeeks = "last [n] weeks",
  LastNMonths = "last [n] months",
  LastNYears = "last [n] years",
  NextNDays = "next [n] days",
  NextNWeeks = "next [n] weeks",
  NextNMonths = "next [n] months",
  NextNYears = "next [n] years"
}

const _translateDate = (dateObj: DateRangeBound) => {
  const { dateType, value } = dateObj;

  const now = new Date();

  switch (dateType) {
    case DateRangeBoundType.SpecificDate:
      return new Date(value as string);
    case DateRangeBoundType.Today:
      return now;
    case DateRangeBoundType.Yesterday:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    case DateRangeBoundType.Tomorrow:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    case DateRangeBoundType.LastNDays:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() - (value as number));
    case DateRangeBoundType.LastNWeeks:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() - (value as number) * 7);
    case DateRangeBoundType.LastNMonths:
      return new Date(now.getFullYear(), now.getMonth() - (value as number), now.getDate());
    case DateRangeBoundType.LastNYears:
      return new Date(now.getFullYear() - (value as number), now.getMonth(), now.getDate());
    case DateRangeBoundType.NextNDays:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + (value as number));
    case DateRangeBoundType.NextNWeeks:
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + (value as number) * 7);
    case DateRangeBoundType.NextNMonths:
      return new Date(now.getFullYear(), now.getMonth() + (value as number), now.getDate());
    case DateRangeBoundType.NextNYears:
      return new Date(now.getFullYear() + (value as number), now.getMonth(), now.getDate());
    case null:
      return now;
    default:
      return null;
  }
};

export const convertDynamicDateToTimestamp = (dateObj: DateRangeBound) => {
  if (!dateObj) return null;
  const dt = _translateDate(dateObj);

  return dt == null ? null : Number(dt);
};

export const validRange = (from: DateRangeBound, to: DateRangeBound) => {
  if (from == null || to == null) return false;
  const [fromDate, toDate] = convertDateRangeToTimestamps({ from, to }) || [];
  if (fromDate == null || toDate == null) return false;

  return fromDate <= toDate;
};

export const convertDateRangeToTimestamps = (dateRange: {
  from: DateRangeBound;
  to: DateRangeBound;
}) => {
  if (!dateRange) return null;

  let result = [
    convertDynamicDateToTimestamp(dateRange.from),
    convertDynamicDateToTimestamp(dateRange.to)
  ];

  // If "To" is not specified, we "invert" the bounds to make it a valid range.
  if (
    dateRange.to.dateType === null &&
    result[1] != null &&
    result[0] != null &&
    result[0] > result[1]
  ) {
    result = [result[1], result[0]];
  }

  return result;
};
